import { PulseMarketData } from '../lib/api';
import { EmptyState } from './EmptyState';

interface Props {
  meetings: PulseMarketData['meetings'];
  articles: PulseMarketData['articles'];
  moodPeriods: PulseMarketData['mood_periods'];
}

const MOOD_RANK: Record<string, number> = {
  panic: 1,
  bear: 2,
  bull: 3,
  euphoric: 4,
};

const MOOD_LABEL: Record<number, string> = {
  1: 'Panic',
  2: 'Bear',
  3: 'Bull',
  4: 'Euphoric',
};

// Translucent band colors for mood periods (rgba using existing token values)
const MOOD_BAND_COLOR: Record<string, string> = {
  panic:    'rgba(239, 68,  68,  0.08)',
  bear:     'rgba(245, 158, 11,  0.08)',
  bull:     'rgba(16,  185, 129, 0.07)',
  euphoric: 'rgba(34,  197, 94,  0.10)',
};

const PAD_DAYS = 4;
const SVG_W = 800;
const SVG_H = 280;
const MARGIN = { top: 24, right: 24, bottom: 36, left: 72 };
const PLOT_W = SVG_W - MARGIN.left - MARGIN.right;
const PLOT_H = SVG_H - MARGIN.top - MARGIN.bottom;

function parseDate(s: string): number {
  return new Date(s).getTime();
}

function formatDateTick(ms: number): string {
  const d = new Date(ms);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function MoodTimeline({ meetings, articles, moodPeriods }: Props) {
  // Collect all dates to determine domain
  const allDates: number[] = [];
  for (const m of meetings) {
    if (m.meeting_date) allDates.push(parseDate(m.meeting_date));
  }
  for (const a of articles) {
    if (a.publication_date) allDates.push(parseDate(a.publication_date));
  }
  for (const p of moodPeriods) {
    allDates.push(parseDate(p.start_date));
    if (p.end_date) allDates.push(parseDate(p.end_date));
  }

  const hasData = allDates.length > 0;

  if (!hasData) {
    return <EmptyState title="No market data yet" hint="Market mood periods, client meetings, and articles will appear here." />;
  }

  const minMs = Math.min(...allDates) - PAD_DAYS * 86400000;
  const maxMs = Math.max(...allDates) + PAD_DAYS * 86400000;
  const timeRange = maxMs - minMs;

  function xScale(ms: number): number {
    return MARGIN.left + ((ms - minMs) / timeRange) * PLOT_W;
  }

  // y: mood rank 1..4, 1 at bottom, 4 at top
  function yScale(rank: number): number {
    return MARGIN.top + PLOT_H - ((rank - 1) / 3) * PLOT_H;
  }

  // Mood period bands
  const bandEls = moodPeriods.map((p) => {
    const x1 = xScale(parseDate(p.start_date));
    const x2 = p.end_date ? xScale(parseDate(p.end_date)) : MARGIN.left + PLOT_W;
    const fill = MOOD_BAND_COLOR[p.mood] ?? 'rgba(255,255,255,0.04)';
    return (
      <rect
        key={p.period_id}
        x={x1}
        y={MARGIN.top}
        width={Math.max(0, x2 - x1)}
        height={PLOT_H}
        fill={fill}
      >
        <title>{p.mood} period{p.notes ? `: ${p.notes}` : ''}</title>
      </rect>
    );
  });

  // Meeting series: filter to those with a mood rank
  const meetingPoints = meetings
    .filter((m) => m.meeting_date && m.sentiment_market && MOOD_RANK[m.sentiment_market])
    .map((m) => ({
      x: xScale(parseDate(m.meeting_date)),
      y: yScale(MOOD_RANK[m.sentiment_market!]),
      label: `${m.client_name} — ${m.meeting_date} — ${m.sentiment_market}`,
      key: m.transcript_id,
    }));

  const meetingPolyline =
    meetingPoints.length > 1
      ? meetingPoints.map((p) => `${p.x},${p.y}`).join(' ')
      : null;

  // Article series: point events only, no line
  const articlePoints = articles
    .filter((a) => a.publication_date && a.market_mood && MOOD_RANK[a.market_mood])
    .map((a) => ({
      x: xScale(parseDate(a.publication_date!)),
      y: yScale(MOOD_RANK[a.market_mood!]),
      label: `${a.headline ?? 'Article'} — ${a.publication_date} — ${a.market_mood}`,
      key: a.article_id,
    }));

  // Y-axis ticks
  const yTicks = [1, 2, 3, 4].map((rank) => ({
    rank,
    y: yScale(rank),
    label: MOOD_LABEL[rank],
  }));

  // X-axis ticks: ~5 evenly spaced
  const tickCount = 5;
  const xTicks = Array.from({ length: tickCount }, (_, i) => {
    const ms = minMs + (i / (tickCount - 1)) * timeRange;
    return { ms, x: xScale(ms), label: formatDateTick(ms) };
  });

  return (
    <div className="mood-timeline-wrap">
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        width="100%"
        height={SVG_H}
        style={{ display: 'block' }}
        aria-label="Market mood timeline"
      >
        {/* Mood period bands */}
        {bandEls}

        {/* Plot border / grid lines */}
        {yTicks.map(({ rank, y }) => (
          <line
            key={rank}
            x1={MARGIN.left}
            y1={y}
            x2={MARGIN.left + PLOT_W}
            y2={y}
            stroke="rgba(255,255,255,0.06)"
            strokeWidth={1}
          />
        ))}

        {/* Y-axis labels */}
        {yTicks.map(({ rank, y, label }) => (
          <text
            key={rank}
            x={MARGIN.left - 8}
            y={y}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize={11}
            fill="var(--text-muted)"
          >
            {label}
          </text>
        ))}

        {/* X-axis labels */}
        {xTicks.map(({ ms, x, label }) => (
          <text
            key={ms}
            x={x}
            y={MARGIN.top + PLOT_H + 20}
            textAnchor="middle"
            fontSize={10}
            fill="var(--text-muted)"
          >
            {label}
          </text>
        ))}

        {/* Meeting polyline */}
        {meetingPolyline && (
          <polyline
            points={meetingPolyline}
            fill="none"
            stroke="var(--info)"
            strokeWidth={2}
            strokeLinejoin="round"
            opacity={0.7}
          />
        )}

        {/* Meeting dots */}
        {meetingPoints.map((p) => (
          <circle key={p.key} cx={p.x} cy={p.y} r={5} fill="var(--info)" opacity={0.9}>
            <title>{p.label}</title>
          </circle>
        ))}

        {/* Article dots (hollow, different color) */}
        {articlePoints.map((p) => (
          <circle
            key={p.key}
            cx={p.x}
            cy={p.y}
            r={4}
            fill="none"
            stroke="var(--warning)"
            strokeWidth={1.5}
            opacity={0.85}
          >
            <title>{p.label}</title>
          </circle>
        ))}
      </svg>

      {/* Legend */}
      <div className="mood-timeline-legend">
        <span className="mood-legend-item">
          <svg width="18" height="10" viewBox="0 0 18 10">
            <line x1="0" y1="5" x2="18" y2="5" stroke="var(--info)" strokeWidth="2" />
            <circle cx="9" cy="5" r="4" fill="var(--info)" />
          </svg>
          Client meetings
        </span>
        <span className="mood-legend-item">
          <svg width="10" height="10" viewBox="0 0 10 10">
            <circle cx="5" cy="5" r="4" fill="none" stroke="var(--warning)" strokeWidth="1.5" />
          </svg>
          Press articles
        </span>
        <span className="mood-legend-item">
          <svg width="14" height="10" viewBox="0 0 14 10">
            <rect x="0" y="0" width="14" height="10" fill="rgba(16,185,129,0.15)" />
          </svg>
          Mood period
        </span>
      </div>
    </div>
  );
}
